import { useEffect, useMemo, useState } from "react";
import { CaretDownOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { Link } from "@tanstack/react-router";
import { Dropdown, Popconfirm } from "antd5";
import classnames from "classnames";
import { Flex, Text } from "styles/utility-components";

import LearnMoreModal from "components/integrations/LearnMoreModal";
import CRMConnectionCard from "components/integrations/managed_integrations/CRMConnectionCard";
import {
  GREENFIELD_PARAGON_CRM_PROVIDERS,
  MIGRATED_PARAGON_CRM_PROVIDERS,
  ParagonCrmProvider,
  useIsParagonCrmEnabled,
} from "components/integrations/managed_integrations/crmProviders";
import IntegrationsCatalog from "components/integrations/managed_integrations/IntegrationsCatalog";
import McpLearnMoreModal from "components/integrations/McpLearnMoreModal";
import { MCP_PROVIDERS, McpProvider } from "components/integrations/mcpProviders";
import {
  getIntegrationIcon,
  getProviderMetadata,
  isNativeProvider,
  type NativeProviderOption,
} from "components/integrations/util";
import Button from "components/ui/button/Button";
import Tooltip from "components/ui/tooltip/Tooltip";
import { MCP_INTEGRATIONS_PAGE_TILES, useVariableValue } from "lib/featureFlags";
import { useConnectedParagonCrm } from "lib/hooks/api/integrations/useConnectedParagonCrm";
import { useFindIntegrationProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import { useInstallProvider } from "lib/hooks/api/integrations/useInstallUrlProvider";
import { useProviderList } from "lib/hooks/api/integrations/useProviderList";
import { useUninstallProvider } from "lib/hooks/api/integrations/useUninstallProvider";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { paragon } from "lib/integrations/paragon";
import { useDialogManager } from "lib/providers/DialogManager";
import { EventNames, TrackingProvider, useTracking } from "lib/tracking";

import css from "./Integrations.module.scss";

/** Why Connect is unavailable on a native tile, which the Paragon card words the same way. */
function connectBlockedReason(
  installedProviderName: string | undefined,
  name: string,
  connectionsUnknown: boolean,
): string | undefined {
  if (connectionsUnknown) {
    return "Stotles could not check which CRM you are connected to. Please reload the page and try again.";
  }
  if (!installedProviderName) return undefined;
  return `You can only connect one CRM at a time. Disconnect ${installedProviderName} to connect to ${name}.`;
}

function ProviderCard({
  provider,
  installedProviderName,
  connectionsUnknown,
}: {
  provider: NativeProviderOption;
  installedProviderName: string | undefined;
  /** Reading the company's connections failed, so whether it already has a CRM is not known. */
  connectionsUnknown: boolean;
}): JSX.Element {
  const { mutate: uninstallProvider } = useUninstallProvider();
  const { mutate: installProvider } = useInstallProvider();
  const metadata = getProviderMetadata(provider.type);
  const dialogManager = useDialogManager();
  const { logEvent } = useTracking();

  return (
    <div key={provider.id}>
      <div className={classnames(css.providerCard)}>
        <Flex column gap={8}>
          {getIntegrationIcon(provider.type, 38)}
          <Text h2>{metadata.name}</Text>
          <Text description>{metadata.description}</Text>
        </Flex>
        <div className={css.buttonContainer}>
          {provider.isInstalled ? (
            <Popconfirm
              title={`Are you sure you want to disconnect from ${metadata.name}?`}
              okText="Disconnect"
              cancelText="Cancel"
              onConfirm={() => {
                logEvent(EventNames.disconnectFromIntegrationClicked, {
                  "Context source": "Integration card",
                  Provider: provider.type,
                });
                uninstallProvider({ providerId: provider.id });
              }}
            >
              <Button buttonStyle="destructive">Disconnect</Button>
            </Popconfirm>
          ) : (
            <Tooltip
              content={connectBlockedReason(
                installedProviderName,
                metadata.name,
                connectionsUnknown,
              )}
            >
              {/* Span so the tooltip still triggers while the button inside is disabled */}
              <span>
                {provider.type === "SALESFORCE" ? (
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      onClick: (key) => {
                        logEvent(EventNames.connectToIntegrationClicked, {
                          "Context source": "Integration card",
                          Provider: provider.type,
                        });
                        installProvider({
                          providerId: provider.id,
                          environment: key.key === "production" ? "PRODUCTION" : "SANDBOX",
                        });
                      },
                      items: [
                        { label: "Production environment", key: "production" },
                        { label: "Sandbox environment", key: "sandbox" },
                      ],
                    }}
                  >
                    <Button disabled={!!installedProviderName || connectionsUnknown}>
                      Connect <CaretDownOutlined className={css.downIcon} />
                    </Button>
                  </Dropdown>
                ) : (
                  <Button
                    onClick={() => {
                      logEvent(EventNames.connectToIntegrationClicked, {
                        "Context source": "Integration card",
                        Provider: provider.type,
                      });
                      installProvider({ providerId: provider.id });
                    }}
                    disabled={!!installedProviderName || connectionsUnknown}
                  >
                    Connect
                  </Button>
                )}
              </span>
            </Tooltip>
          )}
          {provider.isInstalled ? (
            <Link to="/account-management/integrations/setup">
              <Button
                icon="settings01"
                iconProps={{ color: "sysTextDefault" }}
                buttonType="secondary"
              >
                Configure
              </Button>
            </Link>
          ) : (
            <Button
              buttonType="secondary"
              onClick={async () => {
                logEvent(EventNames.learnMoreIntegrationModalOpened, {
                  "Context source": "Integration card",
                  Provider: provider.type,
                });
                void dialogManager.openDialog(LearnMoreModal, {
                  providerType: provider.type,
                });
              }}
            >
              Learn more
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Like ProviderCard, but MCP providers are set up inside the AI tool rather than here, so Connect
// never starts an install. Where the tool can register itself, it opens a modal with the server URL
// and instructions; where it needs a client ID and secret, it goes to the page that issues them and
// carries the same instructions.
function McpProviderCard({ provider }: { provider: McpProvider }): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { logEvent } = useTracking();

  return (
    <div>
      <div className={css.providerCard}>
        <Flex column gap={8}>
          {/* Fixed square box so the brand marks, which vary in visual weight, all occupy the
              same footprint and the tile headings line up across the row. */}
          <McpIconContainer>
            <UIcon icon={provider.icon} color={provider.iconColor} size={32} />
          </McpIconContainer>
          <Text h2>{provider.name}</Text>
          <Text description>{provider.description}</Text>
        </Flex>
        {/* Default primary styling on both, matching the Connect button on the CRM cards. */}
        <div className={css.buttonContainer}>
          {provider.selfServe ? (
            <Button
              as="a"
              to="/account-management/integrations/mcp"
              search={{ tool: provider.id }}
              onClick={() => {
                // The same event the CRM cards log when their Connect starts a connection.
                logEvent(EventNames.connectToIntegrationClicked, {
                  "Context source": "Integration card",
                  Provider: provider.id,
                });
              }}
            >
              Connect
            </Button>
          ) : (
            <Button
              onClick={() => {
                // Same event as the CRM and cloud storage cards; Provider tells them apart.
                logEvent(EventNames.learnMoreIntegrationModalOpened, {
                  "Context source": "Integration card",
                  Provider: provider.id,
                });
                setIsModalOpen(true);
              }}
            >
              Connect
            </Button>
          )}
        </div>
      </div>
      {!provider.selfServe && (
        <McpLearnMoreModal provider={provider} open={isModalOpen} onOpenChange={setIsModalOpen} />
      )}
    </div>
  );
}

// Matches the 38px icons on the CRM cards, so the two sections' tiles stay visually aligned.
const McpIconContainer = styled.div({
  width: 38,
  height: 38,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

// Exported so it can be tested without booting the whole page (which needs the integration-api
// and Paragon). Renders nothing when the flag is off, so there's no stray heading.
export function McpIntegrationsSection(): JSX.Element | null {
  const showMcpTiles = useVariableValue(MCP_INTEGRATIONS_PAGE_TILES, false);

  if (!showMcpTiles) {
    return null;
  }

  return (
    <Flex column gap={16}>
      <Text h3>MCP</Text>
      <div className={css.marketplaceWrapper}>
        {MCP_PROVIDERS.map((provider) => (
          <McpProviderCard provider={provider} key={provider.id} />
        ))}
      </div>
    </Flex>
  );
}

export function Integrations(): JSX.Element {
  // Companies whose admin settings disallow CRM integration keep the rest of this page — MCP and
  // cloud storage are unaffected by that setting. The CRM REST queries are skipped rather than just
  // having their output hidden; the Paragon connection state is still read for the cards below.
  const { company } = useAssertCurrentUser();
  const allowCrmIntegration = company.allowCrmIntegration;

  const {
    data: providerList,
    isInitialLoading: isLoadingProviders,
    isError: providerListFailed,
  } = useProviderList({ enabled: allowCrmIntegration });
  const { data: providerTypeResponse, isError: providerLookupFailed } = useFindIntegrationProvider({
    enabled: allowCrmIntegration,
  });
  const { logEvent } = useTracking();
  const providerType = providerTypeResponse?.providerType;

  /**
   * The CRM this company reaches through Paragon, which answers for every one of them rather than
   * a list per rollout. It requires the service to have recorded the install, not just Paragon to
   * be holding a credential — a reconnect whose recording failed leaves the second without the
   * first, and counting that as a CRM would disable Connect on every card including its own.
   */
  // Gated on the same setting as every other CRM read here: without it, opening the cloud storage
  // or MCP settings would still ask for a CRM the company is not allowed to connect.
  const connectedParagonCrm = useConnectedParagonCrm(allowCrmIntegration);
  // Each migrated CRM rolls out behind its own flag.
  const isParagonCrmEnabled = useIsParagonCrmEnabled();

  /**
   * Reading what the company is already connected to failed. Not the same as finding nothing: a
   * CRM may well be connected and simply unreadable, and every Connect on this page would then
   * offer to replace it without the one-at-a-time rule ever noticing.
   */
  const connectionsUnknown = providerLookupFailed || providerListFailed;

  /**
   * Whether a native provider's tile gives way to its Paragon card. Once that CRM's rollout flag is
   * on that card is the one card this CRM has, and it renders the native connection too — so
   * keeping the tile as well would offer the same CRM twice, under the same name and icon, with no
   * way to tell which one the company is connected through.
   *
   * Conditional on Paragon actually offering the integration here, because that is what decides
   * whether the replacement card renders at all: enabling an integration is per-environment, so a
   * CRM migrated in code but not yet switched on in this Paragon project would otherwise vanish
   * from the page entirely, leaving no way to connect it.
   */
  const paragonOffers = new Set(
    paragon.getIntegrationMetadata().map((integration) => integration.type),
  );
  const isSupersededByParagon = (type: NativeProviderOption["type"]) =>
    MIGRATED_PARAGON_CRM_PROVIDERS.some(
      (provider) =>
        provider.providerType === type &&
        isParagonCrmEnabled(provider) &&
        paragonOffers.has(provider.integrationKey),
    );

  /**
   * The company's live connection to one CRM through our own OAuth app. Handed to that CRM's card
   * so it can offer the move onto Paragon, which is the only way they reach it now that the native
   * tile is gone.
   */
  const nativeInstallOf = (provider: ParagonCrmProvider) => {
    const native = providerList?.find(
      (option) => option.type === provider.providerType && option.isInstalled,
    );
    return native ? { providerId: native.id } : undefined;
  };

  // Only one CRM at a time, and the Paragon-backed connections count even though they are not
  // reported as a native provider.
  const installedProviderName = useMemo(() => {
    if (providerType) return getProviderMetadata(providerType).name;
    return connectedParagonCrm?.name;
  }, [providerType, connectedParagonCrm]);

  // `connected=true` is appended by the CRM OAuth callback. Gated on `providerType` because this
  // effect also runs before that query resolves, and on the CRM setting because a company that
  // can't connect a CRM has no connection for the param to describe.
  useEffect(() => {
    if (!allowCrmIntegration || !providerType) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("connected") === "true") {
      logEvent(EventNames.integrationSuccessfullyConnected, {
        "Page source": "Account Management - Integration connected",
        "Connected provider": providerType,
      });
    }
  }, [logEvent, providerType, allowCrmIntegration]);

  return (
    <TrackingProvider
      data={{
        "Page source": "Account Management - Integrations",
        "Connected provider": providerType || "None",
      }}
    >
      <div className={css.integrations}>
        <Flex column gap={48}>
          <Text h1>Integrations</Text>
          {allowCrmIntegration && (
            <Flex column gap={16}>
              <Text h3>CRM</Text>
              <div className={css.marketplaceWrapper}>
                {/* A CRM reachable through Paragon has one card, below, whichever way the company
                    is connected to it — so its native tile is dropped rather than shown alongside,
                    which would offer the same CRM twice under the same name and icon. The tiles
                    that remain are the CRMs Paragon does not serve. */}
                {providerList
                  ?.filter(isNativeProvider)
                  .filter((provider) => !isSupersededByParagon(provider.type))
                  .map((provider) => (
                    <ProviderCard
                      provider={provider}
                      key={provider.id}
                      installedProviderName={installedProviderName}
                      connectionsUnknown={connectionsUnknown}
                    />
                  ))}
                {[...GREENFIELD_PARAGON_CRM_PROVIDERS, ...MIGRATED_PARAGON_CRM_PROVIDERS].map(
                  (provider) => (
                    <CRMConnectionCard
                      key={provider.integrationKey}
                      provider={provider}
                      installedProviderName={installedProviderName}
                      nativeInstall={nativeInstallOf(provider)}
                      isLoadingConnections={isLoadingProviders}
                      connectionsUnknown={connectionsUnknown}
                    />
                  ),
                )}
              </div>
            </Flex>
          )}

          {/* Sits above Cloud storage: MCP is relevant to more customers than the two
              cloud-storage providers. Also avoids stacking IntegrationsCatalog's own
              marginBottom on the parent gap, which only stays invisible while it is last. */}
          <McpIntegrationsSection />

          <Flex column gap={16}>
            <Text h3>Cloud storage</Text>
            <IntegrationsCatalog />
          </Flex>
        </Flex>
      </div>
    </TrackingProvider>
  );
}
